import {
  Control,
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { Grid, Stack } from '@mui/material'
import InputMask from 'components/FormInputMask'
import { ButtonSecondary } from 'components/ButtonSecondary'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { FormInputAutoComplete } from 'components/FormInputAutoComplete'
import { ItemOption } from 'components/InputSelect'
import FormInput from 'components/FormInput'
import { formatZipCode } from 'utils/text.util'
import { useAddress } from 'hooks/address.hook'
import FormInputCep from 'components/FormInputCEP'

interface FormAddressProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  watch: UseFormWatch<TFieldValues>
  setValue: UseFormSetValue<TFieldValues>
  fields: {
    cep: Path<TFieldValues>
    logradouro: Path<TFieldValues>
    bairro: Path<TFieldValues>
    complemento: Path<TFieldValues>
    numero: Path<TFieldValues>
    cidade: Path<TFieldValues>
    uf: Path<TFieldValues>
  }
  disabled?: boolean
}

export const FormAddress = <TFieldValues extends FieldValues>({
  control,
  watch,
  setValue,

  fields,
  disabled,
}: FormAddressProps<TFieldValues>) => {
  const zipCodeValue = watch(fields.cep)
  const stateIdValue = watch(fields.uf)
  const cityIdValue = watch(fields.cidade)

  const {
    address: { stateOptions, cityOptions, neighborhoodOptions },
    getAddressByZipCode,
  } = useAddress(stateIdValue, cityIdValue)

  const handleFillAddressFields = async (): Promise<void> => {
    if (zipCodeValue) {
      const formattedZipCode = formatZipCode(zipCodeValue)
      const res = await getAddressByZipCode(formattedZipCode)
      if (!res) return
      const neighborhoodValue: string = res.bairroId ? res.bairroId : '0'
      const cityValue: string = res.cidadeId ? res.cidadeId : '0'
      setValue(fields.cep, res.cep as PathValue<TFieldValues, Path<TFieldValues>>)
      setValue(fields.uf, Number(res.ufId) as PathValue<TFieldValues, Path<TFieldValues>>)
      setValue(
        fields.bairro,
        Number(neighborhoodValue) as PathValue<TFieldValues, Path<TFieldValues>>,
      )
      setValue(fields.cidade, Number(cityValue) as PathValue<TFieldValues, Path<TFieldValues>>)
      setValue(fields.logradouro, res.logradouro as PathValue<TFieldValues, Path<TFieldValues>>)
      setValue(fields.complemento, res.complemento as PathValue<TFieldValues, Path<TFieldValues>>)
    }
  }

  const handleDisableSearchZipCode = (): boolean => {
    if (disabled) return true
    let disableSearchZipCode = true
    if (zipCodeValue && zipCodeValue.length >= 7) {
      disableSearchZipCode = false
    }
    return disableSearchZipCode
  }

  return (
    <Grid container spacing={2}>
      <Grid item md={4} xs={12}>
        <Stack direction='row' spacing={1}>
          <FormInputCep
            control={control}
            name={fields.cep}
            label='CEP'
            placeholder='Informe o CEP'
            sx={{ flex: 1 }}
            disabled={disabled}
          />
          <ButtonSecondary
            maxHeight={40}
            startIcon={<SearchRoundedIcon />}
            label='Consultar'
            disabled={handleDisableSearchZipCode()}
            onClick={handleFillAddressFields}
          />
        </Stack>
      </Grid>
      <Grid item md={4} xs={12}>
        <FormInputAutoComplete
          control={control}
          name={fields.uf}
          label='Estado'
          options={stateOptions}
          disabled={disabled}
          getOptionLabel={(option) => option.label}
          getOptionById={(id: string | null, options: ItemOption[]) =>
            options.find((option) => Number(option.value) === Number(id)) || null
          }
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          onChangeValue={(option) => Number(option?.value) || null}
        />
      </Grid>
      <Grid item md={4} xs={12}>
        <FormInputAutoComplete
          control={control}
          name={fields.cidade}
          label='Cidade'
          options={cityOptions}
          disabled={disabled}
          getOptionLabel={(option) => option.label}
          getOptionById={(id: string | null, options: ItemOption[]) =>
            options.find((option) => Number(option.value) === Number(id)) || null
          }
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          onChangeValue={(option) => Number(option?.value) || null}
        />
      </Grid>
      <Grid item md={3} xs={12}>
        <FormInputAutoComplete
          control={control}
          name={fields.bairro}
          label='Bairro'
          options={neighborhoodOptions}
          disabled={disabled}
          getOptionLabel={(option) => option.label}
          getOptionById={(id: string | null, options: ItemOption[]) =>
            options.find((option) => Number(option.value) === Number(id)) || null
          }
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          onChangeValue={(option) => Number(option?.value) || null}
        />
      </Grid>
      <Grid item md={4} xs={12}>
        <FormInput
          control={control}
          name={fields.logradouro}
          label='Logradouro'
          placeholder='Informe o logradouro'
          maxLength={60}
          disabled={disabled}
        />
      </Grid>
      <Grid item md={2} xs={12}>
        <InputMask
          control={control}
          name={fields.numero}
          label='N°'
          mask='0000000000'
          disabled={disabled}
        />
      </Grid>
      <Grid item md={3} xs={12}>
        <FormInput
          control={control}
          name={fields.complemento}
          label='Complemento'
          placeholder='Informe o complemento'
          maxLength={20}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  )
}
